'use client';

// BL-1987 — lets a page ask the left nav to fold to icons while it needs the
// width (the docked lot panel at 1024–1679px, BL-2094), without touching the operator's
// saved nav preference. DashboardShell provides it; Sidebar reads it.

import { createContext, useContext, useEffect, useState, type Dispatch, type ReactNode, type SetStateAction } from 'react';

// Two contexts: the count (read by Sidebar) and the stable setter (read by the
// requesting page), so a count change re-renders only the Sidebar.
const RequestCountContext = createContext(0);
const SetRequestsContext = createContext<Dispatch<SetStateAction<number>> | null>(null);

/**
 * Holds the count of live collapse requests for the dashboard shell.
 *
 * @param props - the shell subtree
 * @returns the provider
 */
export function SidebarAutoCollapseProvider({ children }: { children: ReactNode }) {
  const [requests, setRequests] = useState(0);
  return (
    <SetRequestsContext.Provider value={setRequests}>
      <RequestCountContext.Provider value={requests}>{children}</RequestCountContext.Provider>
    </SetRequestsContext.Provider>
  );
}

/**
 * Whether any page currently asks the nav to fold. False outside a provider.
 *
 * @returns true while at least one request is live
 */
export function useSidebarForcedCollapsed(): boolean {
  return useContext(RequestCountContext) > 0;
}

/**
 * Asks the nav to fold while `active` is true; the request ends on unmount or
 * when `active` goes false. A no-op outside a provider.
 *
 * @param active - whether this page needs the width right now
 */
export function useRequestSidebarCollapse(active: boolean): void {
  const setRequests = useContext(SetRequestsContext);
  useEffect(() => {
    if (!active || !setRequests) return;
    setRequests((n) => n + 1);
    return () => setRequests((n) => n - 1);
  }, [active, setRequests]);
}
